import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bell,
  CalendarCheck,
  CalendarRange,
  ChevronRight,
  Clock,
  CreditCard,
  DoorOpen,
  FileText,
  FolderOpen,
  LayoutDashboard,
  LineChart,
  MapPin,
  Network,
  Package,
  Receipt,
  Settings,
  Star,
  Stethoscope,
  UserCog,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { SectionLabel } from "@/components/account/menu-shared";
import { WorkspaceFooterSections } from "@/components/account/workspace-footer-sections";
import { useDesktopRedirect } from "@/lib/hooks/use-desktop-redirect";

interface SidebarData {
  actionRequiredNotificationCount?: number;
  informationalNotificationCount?: number;
  /** Keyed by facility id — one entry per facility the user is active staff in. */
  facilityStats?: Record<string, unknown>;
}

/** Every facility sub-route in the menu (all exist in routeTree.gen.ts). */
type FacilityMenuRoute =
  | "/account/facility/$facilityId/dashboard"
  | "/account/facility/$facilityId/calendar"
  | "/account/facility/$facilityId/facilities"
  | "/account/facility/$facilityId/providers"
  | "/account/facility/$facilityId/appointments"
  | "/account/facility/$facilityId/patients"
  | "/account/facility/$facilityId/rooms"
  | "/account/facility/$facilityId/staff"
  | "/account/facility/$facilityId/hr"
  | "/account/facility/$facilityId/profile"
  | "/account/facility/$facilityId/reviews"
  | "/account/facility/$facilityId/documents"
  | "/account/facility/$facilityId/inventory"
  | "/account/facility/$facilityId/notifications"
  | "/account/facility/$facilityId/settings/notifications"
  | "/account/facility/$facilityId/billing"
  | "/account/facility/$facilityId/analytics"
  | "/account/facility/$facilityId/financials";

interface FacilityMenuItem {
  to: FacilityMenuRoute | "/account/network";
  label: string;
  icon: LucideIcon;
  badge?: string;
  badgeTone?: "muted" | "destructive" | "warning";
}

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/")({
  component: FacilityRootPage,
});

function FacilityRootPage() {
  const { facilityId } = Route.useParams();
  const { t } = useLingui();

  useDesktopRedirect(`/account/facility/${facilityId}/dashboard`);

  const { data } = useQuery({
    queryKey: ["api", "/api/account/sidebar-data"],
    queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
  });

  const actionRequired = data?.actionRequiredNotificationCount ?? 0;
  const informational = data?.informationalNotificationCount ?? 0;
  const notifTotal = actionRequired + informational;

  // Legacy (account-sidebar.tsx) shows a network-overview link above
  // Dashboard when the user owns/directs 2+ facilities.
  const facilityCount = Object.keys(data?.facilityStats ?? {}).length;

  // Labels match each page's h1 (Branches / Doctors / Rooms & Equipment).
  // Grouped like the desktop side nav (P24): same pages, same links, in
  // sections a receptionist can scan instead of one 18-row list.
  const groups: { id: string; label: string; items: FacilityMenuItem[] }[] = [
    {
      id: "today",
      label: t`Today`,
      items: [
        ...(facilityCount >= 2
          ? ([{ to: "/account/network", label: t`Network`, icon: Network }] as FacilityMenuItem[])
          : []),
        {
          to: "/account/facility/$facilityId/dashboard",
          label: t`Dashboard`,
          icon: LayoutDashboard,
        },
        { to: "/account/facility/$facilityId/calendar", label: t`Calendar`, icon: CalendarRange },
        {
          to: "/account/facility/$facilityId/appointments",
          label: t`Appointments`,
          icon: CalendarCheck,
        },
        { to: "/account/facility/$facilityId/patients", label: t`Patients`, icon: Users },
      ],
    },
    {
      id: "clinic",
      label: t`Clinic`,
      items: [
        { to: "/account/facility/$facilityId/profile", label: t`Clinic profile`, icon: FileText },
        { to: "/account/facility/$facilityId/facilities", label: t`Branches`, icon: MapPin },
        { to: "/account/facility/$facilityId/providers", label: t`Doctors`, icon: Stethoscope },
        { to: "/account/facility/$facilityId/staff", label: t`Staff`, icon: UserCog },
        // Legacy gated HR behind feature `facility.hr.shift_scheduling`; the
        // SPA nav has no feature-gating mechanism yet — ungated for now.
        { to: "/account/facility/$facilityId/hr", label: t`HR / Staff`, icon: Clock },
        {
          to: "/account/facility/$facilityId/rooms",
          label: t`Rooms & Equipment`,
          icon: DoorOpen,
        },
      ],
    },
    {
      id: "operations",
      label: t`Operations`,
      items: [
        { to: "/account/facility/$facilityId/documents", label: t`Documents`, icon: FolderOpen },
        // Legacy gated Inventory behind feature `facility.inventory.stock`;
        // the SPA nav has no feature-gating mechanism yet — ungated for now.
        {
          to: "/account/facility/$facilityId/inventory",
          label: t`Inventory & Supplies`,
          icon: Package,
        },
        { to: "/account/facility/$facilityId/reviews", label: t`Reviews`, icon: Star },
      ],
    },
    {
      id: "money",
      label: t`Finances`,
      items: [
        { to: "/account/facility/$facilityId/financials", label: t`Financials`, icon: Receipt },
        { to: "/account/facility/$facilityId/billing", label: t`Billing`, icon: CreditCard },
        { to: "/account/facility/$facilityId/analytics", label: t`Analytics`, icon: LineChart },
      ],
    },
    {
      id: "settings",
      label: t`Settings`,
      items: [
        {
          to: "/account/facility/$facilityId/notifications",
          label: t`Notifications`,
          icon: Bell,
          badge: notifTotal > 0 ? String(notifTotal) : undefined,
          badgeTone: actionRequired > 0 ? "destructive" : "warning",
        },
        {
          to: "/account/facility/$facilityId/settings/notifications",
          label: t`Notification preferences`,
          icon: Settings,
        },
      ],
    },
    // "API & integrations" (facility `/api`) removed — legacy page gated
    // behind facility.api.access; restore when the facility API page ports.
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Facility</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Manage your clinic, providers, calendar, and operations.</Trans>
        </p>
      </header>

      {groups.map((group) => (
        <section key={group.id} aria-labelledby={`facility-menu-${group.id}`}>
          <div id={`facility-menu-${group.id}`}>
            <SectionLabel>{group.label}</SectionLabel>
          </div>
          <ul className="border-border bg-card divide-border mx-4 divide-y overflow-hidden rounded-xl border">
            {group.items.map((item) => {
              const Icon = item.icon;
              const rowClass =
                "hover:bg-muted/40 flex min-h-[56px] items-center gap-3 px-4 py-3 transition-colors";
              const inner = (
                <>
                  <span className="bg-primary/10 text-primary flex size-9 flex-shrink-0 items-center justify-center rounded-lg">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="text-foreground flex-1 text-sm font-medium">{item.label}</span>
                  {item.badge && (
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5 text-xs font-semibold",
                        item.badgeTone === "destructive"
                          ? "bg-destructive text-destructive-foreground"
                          : item.badgeTone === "warning"
                            ? "bg-warning text-warning-foreground"
                            : "bg-primary/10 text-primary",
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight
                    className="text-muted-foreground size-4 flex-shrink-0"
                    aria-hidden
                  />
                </>
              );
              // Client-side navigation — a plain <a href> reloaded the whole SPA.
              return (
                <li key={item.to}>
                  {item.to === "/account/network" ? (
                    <Link to="/account/network" className={rowClass}>
                      {inner}
                    </Link>
                  ) : (
                    <Link to={item.to} params={{ facilityId }} className={rowClass}>
                      {inner}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <WorkspaceFooterSections />
    </div>
  );
}
